import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  BottomNavigation,
  DefinedBenefitContributionBanner,
  PageContentHeaderDisplay,
  // ShowAllBenefits,
} from 'enrollment/components';
import {
  useBenefitCategoryQuery,
  useEnrollmentStatusQuery,
} from 'enrollment/queries';
import {
  useEnrollmentContextQuery,
  EnrollmentContext,
  PlanYear,
  ENROLLMENT_COMPLETE_STATUS,
  useConfigurationQuery,
  BenefitCategory,
} from 'theSource/queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgLifeEventId,
  selectEnrollmentOrgPlanId,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { setPerPayPeriodCost } from 'enrollment/components/Header/components/EnrollmentNavigation/services';
import {
  selectEnrollmentLatest,
  setEnrollmentLatest,
} from 'enrollment/components/BottomNavigation/services';
import { ROUTES } from 'enrollment/app/routes';
import { isNewStatus } from 'enrollment/components/BottomNavigation/utils';
import { mapCategoriesData } from './utils';
// import { Categories } from './GroupedBenefits';
import { PSBannerWrapper } from '../../../components/Shared';
import { queryKeyGenerator } from '../../../../theSource/utils';
import { setBenefitData } from 'enrollment/services/Benefit/slice';
import { selectHasPreExistingBenefitElections } from 'enrollment/services/CoveragesByCategory';
import { useRestrictionsQuery } from 'enrollment/queries/useRestrictionsQuery';
import { removeBenefitsThatAreRestricted } from './GroupedBenefits/utils';
import { GenericPagePreview } from 'enrollment/features/Shared/PreviewContainers/GenericPagePreview';
import { GenericGroupingContainer } from 'enrollment/features/Shared/PreviewContainers/GenericGroupingContainer';
import { SurveyPopup } from 'enrollment/components/SurveyPopup';
import './BenefitsPreview.css';
import { getPlanYears } from 'enrollment/utils/common/Utils';
import { IneligbleBenefitsModal } from 'enrollment/components/IneligbleBenefitsModal';
import { CategoriesV2 } from '../../BenefitFlowV2';

export interface BenefitsPreviewProps {
  planYears: PlanYear[];
}

type LocationState = { scrollToBenefit?: string };

export const BenefitsPreview = ({ planYears }: BenefitsPreviewProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation() as { state?: LocationState };
  const scrollToBenefit = location.state?.scrollToBenefit;
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContextType === 'life_event' ? orgPlanYearContextId : 0;
  const orgLifeEventID = useSelector(selectEnrollmentOrgLifeEventId);
  const hasPreExistingBenefitElections = useSelector(
    selectHasPreExistingBenefitElections
  );
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [currentCategory, setCurrentCategory] = useState<string>('');
  const [currentCategories, setCurrentCategories] = useState<string[]>([]);
  const [allBenefitsElected, setAllBenefitsElected] = useState<boolean>(false);
  const [completeBenefits, setCompleteBenefits] = useState<boolean>(false);
  const [lastMounted, setLastMounted] = useState(false);
  const [targetMounted, setTargetMounted] = useState(false);


  const {
    enrollmentContext,
    isLoadingEnrollmentContext,
    isFetchingEnrollmentContext,
  } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });
  let contexts: EnrollmentContext[] = [];
  let activeContextIndex = -1;

  if (enrollmentContext) {
    contexts = enrollmentContext?.data as EnrollmentContext[];
    activeContextIndex = Math.max(
      0,
      contexts.findIndex((context) => {
        return (
          !ENROLLMENT_COMPLETE_STATUS.includes(context.status) &&
          enrollmentContextType === context.enrollment_type_lookup_code
        );
      })
    );
    planYears = getPlanYears(
      contexts,
      activeContextIndex
    );
  }

  const [benefitCategories, setBenefitCategories] = useState(
    planYears && mapCategoriesData(planYears)
  );

  

  const { data, isLoading, isFetching, isPreviousData } =
    useBenefitCategoryQuery({
      enrollmentContextType: enrollmentContextType,
      enabled: Boolean(currentIndex >= 0 && enrollmentContextType),
      keepPreviousData: false,
      lumosApiHost: String(lumosApiHost),
      key: queryKeyGenerator(
        `${benefitCategories[currentIndex]?.lookup_code}-${enrollmentContextType}`,
        Number(orgPlanYearContextId)
      ),
      getParamCategory: true,
      includeEligibleDependentsData: true,
      category: benefitCategories[currentIndex]
        ? benefitCategories[currentIndex].lookup_code
        : '',
      orgPlanYearId: orgPlanYearContextId,
    });

  const {
    data: restrictions,
    isLoading: isLoadingLERestirctions,
    isFetching: isFetchingLERestrictions,
  } = useRestrictionsQuery({
    orgLifeEventID: orgLifeEventID,
    orgPlanYearID: orgPlanYearId,
    enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
  });

  let filteredBenefitsData = data;

  const currCategoryLookup = benefitCategories[currentIndex]?.lookup_code;
  const currentCatBenefits = filteredBenefitsData?.data.benefits.filter(
    (benefit) => benefit.benefit.org_benefit_category === currCategoryLookup
  );
  const lastBenefitId = currentCatBenefits?.[currentCatBenefits.length - 1]?.benefit.benefit_lookup_code;

  if (
    data &&
    orgLifeEventID > 0 &&
    orgPlanYearId > 0 &&
    restrictions &&
    enrollmentContextType === 'life_event'
  ) {
    filteredBenefitsData = removeBenefitsThatAreRestricted(
      restrictions.data,
      data
    );
  }

  useEffect(() => {
    if (filteredBenefitsData) {
      dispatch(setBenefitData({ benefitsData: filteredBenefitsData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBenefitsData, dispatch]);

  const { EnrollmentStatusRequest } = useEnrollmentStatusQuery({
    lumosApiHost: String(lumosApiHost),
  });

  const onNext = () => {
    if (currentIndex < benefitCategories.length - 1) {
      const nextCategoryLookup = benefitCategories[currentIndex + 1].name;
      const nextLookupCode = benefitCategories[currentIndex + 1].lookup_code;
      if (isNewStatus(nextLookupCode, enrollmentLatest)) {
        EnrollmentStatusRequest({
          enrollment_type: enrollmentContextType,
          status: nextLookupCode,
        });
      }
      setCurrentCategory(nextCategoryLookup);
      setCurrentCategories([...currentCategories, nextCategoryLookup]);
      setCurrentIndex(currentIndex + 1);
      dispatch(setEnrollmentLatest(nextLookupCode));
    }
  };

  const goToNextPage = () => {
    navigate(`/enrollment${ROUTES.enrollment.benefitDetails.path}`);
  };

  useEffect(() => {
    if (!isPreviousData && filteredBenefitsData) {
      let keep = false;
      for (const benefit of filteredBenefitsData.data.benefits) {
        if (
          benefit.benefit.org_benefit_category ===
          benefitCategories[currentIndex]?.lookup_code
        ) {
          keep = true;
        }
      }

      if (keep === false && currentIndex < benefitCategories.length - 1) {
        setCurrentCategory(benefitCategories[currentIndex + 1].name);
        setCurrentCategories([
          ...currentCategories,
          benefitCategories[currentIndex + 1].name,
        ]);
        setCurrentIndex(currentIndex + 1);
        dispatch(
          setEnrollmentLatest(benefitCategories[currentIndex + 1].lookup_code)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreviousData,
    filteredBenefitsData,
    currentCategory,
    benefitCategories,
    currentIndex,
    currentCategories,
    dispatch,
  ]);

  const findIndexOfCategory = (
    lookupCode: string,
    benefitCategories: BenefitCategory[]
  ) => {
    return benefitCategories.findIndex(
      (category) => category.lookup_code === lookupCode
    );
  };

  const getLookupCodes = (categoryNames: string[]) => {
    return categoryNames.map((categoryName) => {
      const index = benefitCategories.findIndex(
        (category) => category.name === categoryName
      );
      return benefitCategories[index].lookup_code;
    });
  };

  const getCategoriesFromLookup = (
    lookupCode: string,
    benefitCategories: BenefitCategory[]
  ) => {
    const categories = benefitCategories.slice(
      0,
      findIndexOfCategory(lookupCode, benefitCategories) + 1
    );
    return categories.map((category) => category.name);
  };

  const initializeCategories = (benefitCategories: BenefitCategory[]) => {
    if (benefitCategories.length === 0) {
      return;
    }
    const index = findIndexOfCategory(enrollmentLatest, benefitCategories);
    const preBenefitPages = [
      'undefined',
      'family',
      'survey',
      'decision_support',
    ];

    if (preBenefitPages.includes(enrollmentLatest)) {
      setCurrentCategories([benefitCategories[0].name]);
      setCurrentIndex(0);
      setCurrentCategory(benefitCategories[0].name);
      EnrollmentStatusRequest({
        enrollment_type: enrollmentContextType,
        status: benefitCategories[0].lookup_code,
      });
    } else if (enrollmentContextType === 'life_event') {
      const allCategories = benefitCategories.map((category) => category.name);
      const lastCategory =
        benefitCategories[allCategories.length - 1].lookup_code;

      setCurrentCategory(allCategories[allCategories.length - 1]);
      setCurrentCategories(allCategories);
      setCurrentIndex(allCategories.length - 1);
      if (isNewStatus(lastCategory, enrollmentLatest)) {
        EnrollmentStatusRequest({
          enrollment_type: enrollmentContextType,
          status: lastCategory,
        });
      }
    } else if (index > -1) {
      setCurrentCategories(
        getCategoriesFromLookup(enrollmentLatest, benefitCategories)
      );
      setCurrentIndex(index);
      setCurrentCategory(benefitCategories[index].name);
    } else {
      setCurrentCategories(
        getCategoriesFromLookup(
          benefitCategories[0].lookup_code,
          benefitCategories
        )
      );
      setCurrentIndex(0);
      setCurrentCategory(benefitCategories[0].name);
    }
  };

  useEffect(() => {
    if (filteredBenefitsData && !isLoading && !isFetching) {
      if (currentCategories.length === benefitCategories.length) {
        const allBenefitsHaveCoverage =
          filteredBenefitsData?.data.benefits.every(
            (benefit) =>
              Boolean(
                benefit.coverage &&
                  (benefit.benefit.active_enrollment_required &&
                  benefit.benefit.org_benefit_category !== 'survey' // active enrollment check if not survey since these benefits are on a different page
                    ? filteredBenefitsData?.data
                        .active_enrollment_benefit_status[
                        benefit.benefit.org_benefit_id.toString()
                      ] || !benefit.benefit.active_enrollment_required
                    : true)
              ) || benefit.benefit.benefit_lookup_code.includes('unum') // unum benefits don't require checkout
          );
        setAllBenefitsElected(allBenefitsHaveCoverage);

        if (allBenefitsHaveCoverage) {
          setCompleteBenefits(true);
        } else {
          setCompleteBenefits(false);
        }
      }
      if (enrollmentContextType !== 'life_event') {
        dispatch(
          setPerPayPeriodCost(Number(filteredBenefitsData.data.cart_total || 0))
        );
      }
    }
    // it wants a const that will endlessly re-render so will ignore exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBenefitsData, isLoading, dispatch, currentCategories]);


  useEffect(() => {
    const categories = mapCategoriesData(planYears);

    setBenefitCategories(categories);
    initializeCategories(categories);
    // we only want this to render when URL is changed to financial section and back
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    if (!isLoading && currentCategories.length < benefitCategories.length) {
      const currCategoryLookup = benefitCategories[currentIndex]?.lookup_code;
      const currentCatBenefits = filteredBenefitsData?.data.benefits.filter(
        (benefit) => {
          return benefit.benefit.org_benefit_category === currCategoryLookup;
        }
      );

      if (
        currentCatBenefits &&
        currentCatBenefits.length > 0 &&
        currentCatBenefits.every((benefit) => Boolean(benefit.coverage))
      ) {
        onNext();
      }
    }
  });

  
  useEffect(() => {
    setLastMounted(false);
    setTargetMounted(false);
  }, [lastBenefitId, scrollToBenefit]);

  // 3. Update handleBenefitMount
  const handleBenefitMount = (id: string) => {
    if (id === scrollToBenefit) {setTargetMounted(true);}
    if (id === lastBenefitId) {setLastMounted(true);}
  };

  // 4. Scroll only when both are mounted
  useEffect(() => {
    if (lastMounted && targetMounted && scrollToBenefit) {
      let attempts = 0;
      const maxAttempts = 10;
      const delay = 80; // ms
      let cancelled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const tryScroll = () => {
        if (cancelled) {
          return;
        }
        const el = document.getElementById(scrollToBenefit);
        if (el) {
          const rect = el.getBoundingClientRect();
          const offset =
            window.innerWidth >= 744 && window.innerWidth <= 992 ? 180 : 100;
          // Only scroll if the element is visible and has a valid position
          if ((rect.top !== 0 || rect.height !== 0) && rect.top + rect.height > 0) {
            window.scrollTo({
              top: rect.top + window.scrollY - 2 * offset,
              behavior: "smooth",
            });
            return;
          }
        }
        if (++attempts < maxAttempts) {
          timeoutId = setTimeout(tryScroll, delay);
        }
      };

      timeoutId = setTimeout(tryScroll, delay);

      return () => {
        cancelled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [lastMounted, targetMounted, scrollToBenefit]);

  if (
    !benefitCategories ||
    isLoading ||
    isLoadingEnrollmentContext ||
    isFetchingEnrollmentContext ||
    isLoadingLERestirctions ||
    isFetchingLERestrictions ||
    currentCategories.length < 1
  ) {
    return (
      <GenericPagePreview
        title={t('title')}
        containerDataTestId="benefit-preview-page"
        titleDataTestId="benefit-preview-title-loading"
      >
        <GenericGroupingContainer
          id="benefit-loading"
          groupName=""
          loading={true}
          aria-label="Loading..."
        >
          Loading...
        </GenericGroupingContainer>
      </GenericPagePreview>
    );
  }

  return (
    <>
      <DefinedBenefitContributionBanner benefits={filteredBenefitsData} />
      <GenericPagePreview
        title={t('title')}
        containerDataTestId="benefit-preview-page"
        titleDataTestId="benefit-preview-title"
        blockClasses="mt-md-3_5 mt-lg-0 pt-md-4_5 pt-lg-4"
        id="mainContent"
      >
        <div className="page-content-mobile-padding">
          <PageContentHeaderDisplay
            pageContentLookupCode="enroll_benefits"
            displayPageHeader={false}
            orgPlanYearId={orgPlanYearId}
          />
        </div>
        {/* conditional based on at least 1 copied coverage existing */}
        {hasPreExistingBenefitElections[1] && (
          <PSBannerWrapper
            id="copied-elections-banner"
            message={t('copiedElectionsBanner')}
            level="info"
            icon
          />
        )}
        {filteredBenefitsData && (
          <>
            {/* <Categories
            benefitCategories={getLookupCodes(currentCategories)}
            benefitCategoryData={filteredBenefitsData}
            currentBenefitCategory={getLookupCodes([currentCategory])[0]}
            benefitCategoryNames={currentCategories}
            isLoading={isLoading || isFetching || isPreviousData}
          /> */}
            <CategoriesV2
              benefitCategories={getLookupCodes(currentCategories)}
              benefitCategoryData={filteredBenefitsData}
              currentBenefitCategory={getLookupCodes([currentCategory])[0]}
              benefitCategoryNames={currentCategories}
              isLoading={isLoading || isFetching || isPreviousData}
              allCategories={benefitCategories.map(
                (category) => category.lookup_code
              )}
              onBenefitMount={handleBenefitMount}
            />
            <SurveyPopup
              categoryResponse={filteredBenefitsData}
              currentCategory={getLookupCodes([currentCategory])[0]}
            />
          </>
        )}
        {/* {currentIndex < benefitCategories.length - 1 && (
        <div className="text-center text-md-left mt-md-4">
          <ps-button
            id="benefits-continue-button"
            label={t('LooksGood')}
            level="primary"
            onClick={onNext}
          />
        </div>
      )} */}
        <IneligbleBenefitsModal
          enrollmentContextType={enrollmentContextType}
          orgPlanYearContextId={orgPlanYearContextId}
          benefitCategories={benefitCategories}
          currentIndex={currentIndex}
          data={filteredBenefitsData}
        />
        {/* <ShowAllBenefits
        currentCategory={currentCategory}
        allCategories={benefitCategories.map((category) => category.name)}
        allCategoryLookups={benefitCategories.map(
          (category) => category.lookup_code
        )}
        setCurrentCategory={setCurrentCategory}
        setCurrentCategories={setCurrentCategories}
        setCurrentIndex={setCurrentIndex}
      /> */}
        {!completeBenefits &&
          currentCategories.length === benefitCategories.length && (
            <PSBannerWrapper
              id="incomplete-elections-banner"
              message={t('incompleteElectionsBanner')}
              level="info"
              icon
            />
          )}
        <BottomNavigation
          disableButton={!allBenefitsElected}
          translatedPage="enrollment.benefits"
          onClickEvent={goToNextPage}
        />
      </GenericPagePreview>
    </>
  );
};
